import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Smartphone, Monitor, Share, MoreVertical, Plus, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { Card } from '../components/ui/card';

const InstalarApp = () => {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) setPlatform('ios');
    else if (/Android/.test(ua)) setPlatform('android');
    else if (/Mac/.test(ua)) setPlatform('mac');
    else if (/Win/.test(ua)) setPlatform('windows');
    else setPlatform('desktop');

    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const toggle = (key) => setExpandedSection(expandedSection === key ? null : key);

  const Step = ({ num, children }) => (
    <div className="flex gap-3 items-start">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center text-sm font-bold">{num}</span>
      <p className="text-sm text-gray-700 pt-0.5">{children}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="bg-[#1A4D2E] text-white px-6 py-10 text-center">
        <button onClick={() => navigate(-1)} className="absolute left-4 top-6 hover:bg-white/10 rounded-full p-2 transition-all" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex justify-center mb-4">
          <img src="/logos/logo_raizes_white.png" alt="Raizes" className="h-16 object-contain" />
        </div>
        <h1 className="font-nunito font-bold text-2xl">Instalar Ministério Raízes</h1>
        <p className="text-white/70 text-sm mt-1">Tenha o app na tela inicial do seu dispositivo</p>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {isInstalled && (
          <Card className="bg-emerald-50 border-emerald-200 rounded-xl p-4" data-testid="already-installed">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800 text-sm">App ja instalado!</p>
                <p className="text-emerald-700 text-xs">Voce ja esta usando o app no modo instalado.</p>
              </div>
            </div>
          </Card>
        )}

        {deferredPrompt && (
          <button onClick={handleInstall} data-testid="install-btn"
            className="w-full bg-[#1A4D2E] hover:bg-[#163f25] text-white rounded-xl py-4 px-6 flex items-center justify-center gap-3 font-bold text-lg shadow-lg transition-all active:scale-[0.98]">
            <Download className="w-6 h-6" />
            Instalar Agora
          </button>
        )}

        {/* Android */}
        <Card className={`rounded-xl overflow-hidden border ${platform === 'android' ? 'border-[#1A4D2E] shadow-md' : 'border-gray-200'}`}>
          <button onClick={() => toggle('android')} className="w-full flex items-center justify-between p-4" data-testid="android-section">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-[#1A4D2E]" />
              <span className="font-semibold text-[#1A4D2E]">Android (Chrome)</span>
              {platform === 'android' && <span className="bg-[#E1ECE5] text-[#1A4D2E] text-[10px] font-bold px-2 py-0.5 rounded-full">SEU DISPOSITIVO</span>}
            </div>
            {expandedSection === 'android' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {expandedSection === 'android' && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
              <Step num="1">Abra este site no <strong>Google Chrome</strong></Step>
              <Step num="2">Toque no icone <strong>tres pontos</strong> <MoreVertical className="inline w-4 h-4" /> no canto superior direito</Step>
              <Step num="3">Selecione <strong>"Adicionar a tela inicial"</strong> ou <strong>"Instalar app"</strong></Step>
              <Step num="4">Confirme tocando em <strong>"Adicionar"</strong></Step>
              <div className="bg-[#F2F7F4] rounded-lg p-3 mt-2">
                <p className="text-xs text-[#1A4D2E]">O app aparecera na sua tela inicial como um aplicativo normal, com icone proprio e sem barra do navegador.</p>
              </div>
            </div>
          )}
        </Card>

        {/* iOS */}
        <Card className={`rounded-xl overflow-hidden border ${platform === 'ios' ? 'border-[#1A4D2E] shadow-md' : 'border-gray-200'}`}>
          <button onClick={() => toggle('ios')} className="w-full flex items-center justify-between p-4" data-testid="ios-section">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-[#1A4D2E]" />
              <span className="font-semibold text-[#1A4D2E]">iPhone / iPad (Safari)</span>
              {platform === 'ios' && <span className="bg-[#E1ECE5] text-[#1A4D2E] text-[10px] font-bold px-2 py-0.5 rounded-full">SEU DISPOSITIVO</span>}
            </div>
            {expandedSection === 'ios' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {expandedSection === 'ios' && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
              <Step num="1">Abra este site no <strong>Safari</strong> (navegador nativo do iPhone)</Step>
              <Step num="2">Toque no botao <strong>Compartilhar</strong> <Share className="inline w-4 h-4" /> na barra inferior</Step>
              <Step num="3">Role para baixo e toque em <strong>"Adicionar a Tela de Inicio"</strong> <Plus className="inline w-4 h-4" /></Step>
              <Step num="4">Confirme tocando em <strong>"Adicionar"</strong></Step>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                <p className="text-xs text-amber-800"><strong>Importante:</strong> No iPhone, o app so pode ser instalado pelo Safari. Chrome e outros navegadores nao suportam essa funcao no iOS.</p>
              </div>
            </div>
          )}
        </Card>

        {/* Desktop */}
        <Card className={`rounded-xl overflow-hidden border ${(platform === 'windows' || platform === 'mac' || platform === 'desktop') ? 'border-[#1A4D2E] shadow-md' : 'border-gray-200'}`}>
          <button onClick={() => toggle('desktop')} className="w-full flex items-center justify-between p-4" data-testid="desktop-section">
            <div className="flex items-center gap-3">
              <Monitor className="w-5 h-5 text-[#1A4D2E]" />
              <span className="font-semibold text-[#1A4D2E]">Computador (Chrome / Edge)</span>
              {(platform === 'windows' || platform === 'mac' || platform === 'desktop') && <span className="bg-[#E1ECE5] text-[#1A4D2E] text-[10px] font-bold px-2 py-0.5 rounded-full">SEU DISPOSITIVO</span>}
            </div>
            {expandedSection === 'desktop' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {expandedSection === 'desktop' && (
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
              <Step num="1">Abra este site no <strong>Google Chrome</strong> ou <strong>Microsoft Edge</strong></Step>
              <Step num="2">Clique no icone de <strong>instalacao</strong> <Download className="inline w-4 h-4" /> na barra de enderecos (ao lado da URL)</Step>
              <Step num="3">Confirme clicando em <strong>"Instalar"</strong></Step>
              <div className="bg-[#F2F7F4] rounded-lg p-3 mt-2">
                <p className="text-xs text-[#1A4D2E]">O app abrira em uma janela propria, sem barras do navegador, como um programa nativo.</p>
              </div>
            </div>
          )}
        </Card>

        {/* Info */}
        <Card className="rounded-xl border border-gray-200 p-4 mt-6">
          <h3 className="font-semibold text-[#1A4D2E] text-sm mb-2">Por que instalar?</h3>
          <ul className="space-y-2 text-xs text-gray-600">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1A4D2E] flex-shrink-0 mt-0.5" /> Acesso rapido pela tela inicial</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1A4D2E] flex-shrink-0 mt-0.5" /> Funciona em tela cheia, sem barra do navegador</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1A4D2E] flex-shrink-0 mt-0.5" /> Nao precisa baixar das lojas</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1A4D2E] flex-shrink-0 mt-0.5" /> Sempre atualizado automaticamente</li>
          </ul>
        </Card>

        <p className="text-center text-xs text-gray-400 mt-4 pb-8">
          Versao 1.0.0 - Ministerio Raizes
        </p>
      </div>
    </div>
  );
};

export default InstalarApp;
